import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { Card, SectionTitle } from "../ui";
import { api, type BodyMeasurement } from "@/api";
import { useUI } from "@/lib/theme";
import { fmtNum } from "@/lib/format";

type Field = {
  key: keyof BodyMeasurement;
  labelKey: string;
  unit: string;
  color: string;
};

const FIELDS: Field[] = [
  { key: "weight_kg",    labelKey: "body_weight", unit: "kg",  color: "#f59e0b" },
  { key: "body_fat_pct", labelKey: "body_fat",    unit: "%",   color: "#ef4444" },
  { key: "muscle_kg",    labelKey: "body_muscle", unit: "kg",  color: "#3b82f6" },
  { key: "waist_cm",     labelKey: "body_waist",  unit: "cm",  color: "#8b5cf6" },
  { key: "chest_cm",     labelKey: "body_chest",  unit: "cm",  color: "#06b6d4" },
  { key: "hip_cm",       labelKey: "body_hip",    unit: "cm",  color: "#ec4899" },
  { key: "arm_cm",       labelKey: "body_arm",    unit: "cm",  color: "#10b981" },
  { key: "thigh_cm",     labelKey: "body_thigh",  unit: "cm",  color: "#f97316" },
];

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function Input({
  label,
  unit,
  value,
  onChange,
}: {
  label: string;
  unit: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-0.5">
      <span className="text-xs font-medium text-muted">
        {label} <span className="text-muted/60">({unit})</span>
      </span>
      <input
        type="number"
        min="0"
        step="0.1"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-line bg-panel px-2 py-1.5 text-sm text-fg placeholder:text-muted/40 focus:outline-none focus:ring-1 focus:ring-accent"
        placeholder="—"
      />
    </label>
  );
}

export default function BodyComposition() {
  const { t, loc } = useUI();
  const [rows, setRows] = useState<BodyMeasurement[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [chartField, setChartField] = useState<keyof BodyMeasurement>("weight_kg");

  // Form state
  const [date, setDate] = useState(todayStr());
  const [fields, setFields] = useState<Record<string, string>>({});

  const load = () => {
    api.body().then((r) => setRows(r.body)).catch(() => setRows([]));
  };
  useEffect(load, []);

  const handleSave = async () => {
    if (!date) return;
    setSaving(true);
    try {
      const payload: Record<string, unknown> = { date };
      for (const f of FIELDS) {
        const v = fields[f.key as string];
        if (v !== undefined && v !== "") payload[f.key as string] = Number(v);
      }
      const note = fields["note"];
      if (note) payload["note"] = note;
      await api.saveBody(payload as Parameters<typeof api.saveBody>[0]);
      setFields({});
      load();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeleting(id);
    try {
      await api.deleteBody(id);
      setRows((prev) => prev?.filter((r) => r.id !== id) ?? null);
    } finally {
      setDeleting(null);
    }
  };

  const chartData = (rows ?? []).map((r) => ({
    date: r.date,
    v: r[chartField] as number | null,
  })).filter((d) => d.v != null);

  const activeField = FIELDS.find((f) => f.key === chartField) ?? FIELDS[0]!;

  return (
    <div className="space-y-6">
      <SectionTitle>{t("body_title")}</SectionTitle>

      {/* Formulario de entrada */}
      <Card>
        <div className="mb-3 flex items-center gap-3">
          <label className="flex flex-col gap-0.5">
            <span className="text-xs font-medium text-muted">{t("body_date")}</span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="rounded-lg border border-line bg-panel px-2 py-1.5 text-sm text-fg focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {FIELDS.map((f) => (
            <Input
              key={f.key as string}
              label={t(f.labelKey as Parameters<typeof t>[0])}
              unit={f.unit}
              value={fields[f.key as string] ?? ""}
              onChange={(v) => setFields((prev) => ({ ...prev, [f.key as string]: v }))}
            />
          ))}
          <label className="col-span-2 flex flex-col gap-0.5 sm:col-span-3 md:col-span-4">
            <span className="text-xs font-medium text-muted">{t("body_note")}</span>
            <input
              type="text"
              maxLength={500}
              value={fields["note"] ?? ""}
              onChange={(e) => setFields((prev) => ({ ...prev, note: e.target.value }))}
              className="w-full rounded-lg border border-line bg-panel px-2 py-1.5 text-sm text-fg placeholder:text-muted/40 focus:outline-none focus:ring-1 focus:ring-accent"
              placeholder="Opcional…"
            />
          </label>
        </div>
        <button
          onClick={() => void handleSave()}
          disabled={saving}
          className="mt-4 rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
        >
          {saving ? "…" : t("body_add")}
        </button>
      </Card>

      {/* Gráfica */}
      {rows !== null && rows.length > 1 && (
        <Card>
          <div className="mb-3 flex flex-wrap gap-1.5">
            {FIELDS.map((f) => (
              <button
                key={f.key as string}
                onClick={() => setChartField(f.key)}
                className={`rounded-full border px-2.5 py-0.5 text-xs font-medium transition ${
                  chartField === f.key
                    ? "border-accent bg-accent/10 text-accent"
                    : "border-line text-muted hover:text-fg"
                }`}
              >
                {t(f.labelKey as Parameters<typeof t>[0])}
              </button>
            ))}
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-line, #e2e8f0)" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d: string) => d.slice(5)} />
              <YAxis
                tick={{ fontSize: 11 }}
                tickFormatter={(v: number) => fmtNum(v, 1, loc)}
                unit={` ${activeField.unit}`}
                width={48}
              />
              <Tooltip
                formatter={(v: number) => [`${fmtNum(v, 1, loc)} ${activeField.unit}`, t(activeField.labelKey as Parameters<typeof t>[0])]}
                labelFormatter={(l: string) => l}
              />
              <Line
                type="monotone"
                dataKey="v"
                stroke={activeField.color}
                strokeWidth={2}
                dot={{ r: 3, fill: activeField.color }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Tabla de registros */}
      {rows === null ? (
        <p className="text-sm text-muted">{"Cargando…"}</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted">{t("body_empty")}</p>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-xs text-muted">
                  <th className="py-1.5 pr-4 text-left font-medium">{t("body_date")}</th>
                  {FIELDS.map((f) => (
                    <th key={f.key as string} className="py-1.5 pr-3 text-right font-medium">
                      {t(f.labelKey as Parameters<typeof t>[0])}
                    </th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {[...rows].reverse().map((r) => (
                  <tr key={r.id} className="border-b border-line/50 last:border-0">
                    <td className="py-1.5 pr-4 text-muted">{r.date}</td>
                    {FIELDS.map((f) => {
                      const v = r[f.key] as number | null;
                      return (
                        <td key={f.key as string} className="py-1.5 pr-3 text-right tabular-nums">
                          {v != null ? fmtNum(v, 1, loc) : "—"}
                        </td>
                      );
                    })}
                    <td className="py-1.5 pl-2">
                      <button
                        onClick={() => void handleDelete(r.id)}
                        disabled={deleting === r.id}
                        className="text-xs text-rose-500 opacity-60 transition hover:opacity-100 disabled:opacity-30"
                        title={t("body_del")}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
